// Grants (docs/superpowers/specs/2026-10-23-relay-pr-connections-design.md,
// docs/superpowers/specs/2026-11-01-subjects-design.md): which subject (a
// pull request, a compare or a branch; relaySubject.ts), and which of its
// data, each paired agent may read.
// The browser is authoritative: the page (relay.ts) and the relay worker
// (relay-serve.ts) both read the grants here, in the user's relay database,
// and answer a query only within them. The helper keeps a mirror, which
// `sync` rewrites and from which it revokes what the browser doesn't hold.
import { read, write } from './relay-db';
import { categoriesFor, isTool, KIND_NAMES, noFacet, parseSubject, pullKey, readKey, RELAY_CATEGORIES, sameSubject, toolCategory, type RelayCategory, type RelayTool, type SubjectKey, type SubjectKind } from './relaySubject';
export { RELAY_CATEGORIES, type RelayCategory, type RelayTool, type SubjectKey };

/** A grant's categories: at least one, in RELAY_CATEGORIES's order, each one its subject's kind allows. */
export type Scope = readonly [RelayCategory, ...RelayCategory[]];
/**
 * Where one pairing's grant on one subject stands, between this browser (which
 * decides) and the helper (which mirrors it):
 * - `granted`: in force here; `acked` once the helper acknowledged this scope.
 * - `refused`: the helper refused it for good, so this browser refuses it too
 *   until it is granted again; `revoking` while the helper still holds it and
 *   is to be told to drop it.
 * - `revoking`: revoked here, and the helper is still to be told.
 */
export type GrantState =
  | { kind: 'granted'; scope: Scope; acked: boolean }
  | { kind: 'refused'; scope: Scope; reason: string; revoking: boolean }
  | { kind: 'revoking' };
/** A stored grant record: `updated` grows with each change, so `sync` can tell a newer change from the one it sent. */
export interface GrantRecord { connection: string; subject: SubjectKey; updated: number; state: GrantState }
/**
 * A grant as the page, the worker and the tab's reads see it: one in force
 * (`granted`, `acked` once the agent's computer has it) or one its computer
 * refused (`refused`, with why). A revocation still to send is no grant.
 */
export type RelayGrant = { connection: string; subject: SubjectKey; scope: Scope; updated: number } & ({ status: 'granted'; acked: boolean } | { status: 'refused'; reason: string });
export interface RelayQuery { id: string; connection: string; tool: RelayTool; subject: SubjectKey; args: Record<string, unknown> }
// The helper's other wire shapes (plugins/changeling/testdata/contract.json),
// shared by the page (relay.ts) and the relay worker (relay-serve.ts).
/** A pairing: this browser with one agent route (`harness` on one computer). */
/** A pairing with an agent; `paired`, when this browser first paired with it (none for one paired before it was kept). */
export interface RelayConnection { id: string; peer: string; route: string; relay?: string; name: string; harness: string; paired?: number }
/** A helper's own record of a grant (its mirror). */
export interface HelperGrant { subject: SubjectKey; scope: RelayCategory[]; updated: number }
/**
 * An agent asking for a grant on `subject`, as `serve` returns it, or on
 * the history a `locator` names (its wire text, relaySubject.ts
 * `parseLocator`), which this browser resolves (`resolve`) before the
 * person is asked; `request` is the request started in Changeling it
 * carries, if any.
 */
export type WireGrantRequest = { id: string; request?: string; fingerprint: string; name: string; created: number } & ({ subject: SubjectKey; locator?: undefined } | { locator: string; subject?: undefined });
/** A grant request as tabs get it, with the pairing it came from. */
export type GrantRequest = WireGrantRequest & { connection: string };

/** The subjects one pairing may be granted at once, as the helper allows. */
export const MAX_GRANTS = 100;
/** The life of a grant request or a request started in Changeling, as the helper's. */
export const REQUEST_TTL_MS = 10 * 60 * 1000;
/**
 * Where the relay database keeps when this browser last heard from a
 * pairing's agent (this browser's clock, ms): its own key per pairing, so
 * the worker's writes and a tab's never overwrite each other's. Written by
 * the worker as it serves (relay-serve.ts) and on pairing; dropped with it.
 */
export const seenKey = (connection: string) => `seen:${connection}`;

/** The message of a rejection, without the "Error: " that `String(e)` adds. */
export function errorMessage(e: unknown): string { return e instanceof Error ? e.message : String(e); }

/** A request code (of a request started in Changeling) as `XXXX-XXXX`, from either spelling in any case; null when it isn't one. */
export function requestCode(req: unknown): string | null {
  if (typeof req !== 'string') return null;
  const plain = req.toUpperCase().replace(/^([A-Z2-7]{4})-([A-Z2-7]{4})$/, '$1$2');
  return /^[A-Z2-7]{8}$/.test(plain) ? `${plain.slice(0, 4)}-${plain.slice(4)}` : null;
}

/** The kind of the subject `key` names. */
export const kindOf = (key: SubjectKey): SubjectKind => parseSubject(key)!.kind;

/** `scope` in canonical order, without repeats; refused when empty, unknown, or not allowed for `kind`. */
export function checkScope(scope: readonly string[], kind: SubjectKind): Scope {
  if (!Array.isArray(scope)) throw new Error('Choose at least one kind of data to share.');
  const unknown = scope.find(c => !RELAY_CATEGORIES.includes(c as RelayCategory));
  if (unknown !== undefined) throw new Error(`Unknown category: ${String(unknown).slice(0, 40)}`);
  const wrong = scope.find(c => !categoriesFor(kind).includes(c as RelayCategory));
  if (wrong !== undefined) throw new Error(`A ${KIND_NAMES[kind]} has no ${wrong} to share.`);
  const result = RELAY_CATEGORIES.filter(c => scope.includes(c));
  if (!result.length) throw new Error('Choose at least one kind of data to share.');
  return result as unknown as Scope;
}

/**
 * The one rule a query is answered by, wherever it is checked (the worker
 * before and after a tab answers, the tab before it reads, relayTools
 * before and after its reads): `connection` may answer `tool` on `subject`
 * when the subject's kind has something for the tool to read, the
 * connection holds a grant on that subject, its computer has not refused
 * it, and its scope has the tool's category. Otherwise, why not.
 */
export function authorize(grants: readonly RelayGrant[], connection: string, tool: unknown, subject: unknown): { ok: true; grant: Extract<RelayGrant, { status: 'granted' }> } | { ok: false; why: string } {
  if (!isTool(tool)) return { ok: false, why: `Unknown tool: ${String(tool).slice(0, 40)}` };
  const key = readKey(subject);
  if (!key) return { ok: false, why: 'The query names no subject.' };
  const kind = kindOf(key), category = toolCategory(tool, kind);
  if (!category) return { ok: false, why: noFacet(tool, kind) };
  const grant = grants.find(g => g.connection === connection && sameSubject(g.subject, key));
  if (!grant) return { ok: false, why: `This agent has no grant on ${key}.` };
  switch (grant.status) {
    case 'refused': return { ok: false, why: `This agent's grant on ${key} was refused by its computer: ${grant.reason}` };
    case 'granted': return grant.scope.includes(category) ? { ok: true, grant } : { ok: false, why: `This agent's grant on ${key} does not include ${category}.` };
  }
}

/** Why `connection` may not answer `tool` on `subject` under `grants` (`authorize`), or null when it may. */
export function refusal(grants: readonly RelayGrant[], connection: string, tool: unknown, subject: unknown): string | null {
  const a = authorize(grants, connection, tool, subject);
  return a.ok ? null : a.why;
}

/** `scope` as a grant's on a subject of `kind`: categories it allows, in order, at least one; null otherwise. */
function readScope(scope: unknown, kind: SubjectKind): Scope | null {
  if (!Array.isArray(scope) || scope.some(c => !categoriesFor(kind).includes(c))) return null;
  const ordered = RELAY_CATEGORIES.filter(c => scope.includes(c));
  return ordered.length ? ordered as unknown as Scope : null;
}
/** A pull request as records named one before subjects (`{ owner, repo, number }`), as its key. */
function legacyPull(pr: unknown): SubjectKey | null {
  const p = pr as { owner?: unknown; repo?: unknown; number?: unknown } | null;
  if (typeof p !== 'object' || p === null || typeof p.owner !== 'string' || typeof p.repo !== 'string' || typeof p.number !== 'number') return null;
  return readKey(pullKey(p.owner, p.repo, p.number));
}
/**
 * A stored grant record as it is now, from any shape ever stored: its
 * subject (`subject`, or the pull request it named before, `pr`), and the
 * lifecycle (`state`), or the flags it was kept in before (`scope`, empty
 * for a revocation to send; `synced`; `failed`). Null for a damaged record,
 * or for one that holds nothing (an acknowledged revocation).
 */
export function decodeGrant(raw: unknown): GrantRecord | null {
  const r = raw as { connection?: unknown; subject?: unknown; pr?: unknown; updated?: unknown; state?: unknown; scope?: unknown; synced?: unknown; failed?: unknown } | null;
  if (typeof r !== 'object' || r === null || typeof r.connection !== 'string' || typeof r.updated !== 'number') return null;
  const subject = 'subject' in r ? readKey(r.subject) : legacyPull(r.pr);
  if (!subject) return null;
  const kind = kindOf(subject);
  const state = 'state' in r ? readState(r.state, kind) : legacyState(r.scope, r.synced, r.failed, kind);
  return state ? { connection: r.connection, subject, updated: r.updated, state } : null;
}
function readState(raw: unknown, kind: SubjectKind): GrantState | null {
  const s = raw as { kind?: unknown; scope?: unknown; acked?: unknown; reason?: unknown; revoking?: unknown } | null;
  if (typeof s !== 'object' || s === null) return null;
  if (s.kind === 'revoking') return { kind: 'revoking' };
  const scope = readScope(s.scope, kind);
  if (!scope) return null;
  if (s.kind === 'granted' && typeof s.acked === 'boolean') return { kind: 'granted', scope, acked: s.acked };
  if (s.kind === 'refused' && typeof s.reason === 'string' && typeof s.revoking === 'boolean') return { kind: 'refused', scope, reason: s.reason, revoking: s.revoking };
  return null;
}
function legacyState(rawScope: unknown, synced: unknown, failed: unknown, kind: SubjectKind): GrantState | null {
  if (Array.isArray(rawScope) && rawScope.length === 0) return synced === false ? { kind: 'revoking' } : null;
  const scope = readScope(rawScope, kind);
  if (!scope) return null;
  if (typeof failed === 'string') return { kind: 'refused', scope, reason: failed, revoking: synced === false };
  return { kind: 'granted', scope, acked: synced === true };
}
/** What `record` is as a grant (`RelayGrant`): none while only its revocation is still to send. */
export function grantOf(record: GrantRecord): RelayGrant | null {
  const { connection, subject, updated, state } = record;
  switch (state.kind) {
    case 'granted': return { connection, subject, updated, scope: state.scope, status: 'granted', acked: state.acked };
    case 'refused': return { connection, subject, updated, scope: state.scope, status: 'refused', reason: state.reason };
    case 'revoking': return null;
  }
}
/** The scope `record` grants: none for a revocation. */
export function scopeOf(record: GrantRecord): readonly RelayCategory[] {
  return record.state.kind === 'revoking' ? [] : record.state.scope;
}

/** Every grant record, revocations still to send included, read through `decodeGrant`. */
export async function grantRecords(user: string): Promise<GrantRecord[]> {
  return (await read<unknown[]>(user, 'grants') ?? []).flatMap(r => decodeGrant(r) ?? []);
}

/** The grants, granted or refused: every record but a revocation still to send. */
export async function liveGrants(user: string): Promise<RelayGrant[]> {
  return (await grantRecords(user)).flatMap(r => grantOf(r) ?? []);
}

/** Runs `fn` holding the user's relay state lock, as relay.ts's writes do. */
export function locked<T>(user: string, fn: () => Promise<T>): Promise<T> {
  return navigator.locks.request(`remote-relay-state:${user}`, fn);
}

/** A grant of `scope` (checked by `checkScope` for its subject), still to send unless `acked`; an empty scope is a revocation to send. */
export function granting(scope: Scope | readonly [], acked = false): GrantState {
  return scope.length ? { kind: 'granted', scope: scope as Scope, acked } : { kind: 'revoking' };
}

/**
 * Records `state` for `connection` on `subject` (see `granting`), keeping
 * the subject's spelling of an earlier grant. Call it holding the state lock.
 */
export async function putGrant(user: string, connection: string, subject: SubjectKey, state: GrantState): Promise<void> {
  const all = await grantRecords(user);
  const old = all.find(g => g.connection === connection && sameSubject(g.subject, subject));
  // Strictly increasing, so `sync` can tell a newer change from the one it sent.
  const updated = Math.max(Date.now(), (old?.updated ?? 0) + 1);
  await write(user, 'grants', [...all.filter(g => g !== old), { connection, subject: old?.subject ?? subject, updated, state }]);
}

const sameScope = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every(c => b.includes(c));
/**
 * Brings this browser's records for `connection` in line with a helper
 * reply's `grants` (its mirror), as changes for `sync` to send: a grant
 * acknowledged before that the mirror lacks, or holds with another scope,
 * is sent again; a refused grant the mirror still holds, and a mirror grant
 * this browser has no record of, are revoked (`sync` sends those first).
 * Records with a change still to send are left to it. Nothing for a pairing
 * no longer known. Resolves with whether it queued any. Call it holding the
 * state lock.
 */
export async function reconcileMirror(user: string, connection: string, reply: unknown): Promise<boolean> {
  const list = (reply as { grants?: unknown } | null)?.grants;
  if (!Array.isArray(list)) return false;
  // A pairing forgotten while its reply was on the way keeps no records.
  if (!(await read<{ id: string }[]>(user, 'connections') ?? []).some(c => c.id === connection)) return false;
  const mirror = list.filter((g): g is { subject: SubjectKey; scope: string[] } => readKey((g as { subject?: unknown })?.subject) !== null && Array.isArray((g as { scope?: unknown }).scope));
  const all = await grantRecords(user);
  const now = Date.now();
  let queued = false;
  const next = all.map((g): GrantRecord => {
    if (g.connection !== connection) return g;
    const there = mirror.find(m => sameSubject(m.subject, g.subject));
    const s = g.state;
    let state: GrantState | null = null;
    switch (s.kind) {
      case 'granted': if (s.acked && (!there || !sameScope(there.scope, s.scope))) state = { ...s, acked: false }; break;
      case 'refused': if (!s.revoking && there) state = { ...s, revoking: true }; break;
      case 'revoking': break;
    }
    if (!state) return g;
    queued = true;
    return { ...g, state, updated: Math.max(now, g.updated + 1) };
  });
  for (const m of mirror) {
    if (next.some(g => g.connection === connection && sameSubject(g.subject, m.subject))) continue;
    next.push({ connection, subject: m.subject, updated: now, state: { kind: 'revoking' } });
    queued = true;
  }
  if (queued) await write(user, 'grants', next);
  return queued;
}

export interface SyncOptions {
  /** Throws to abandon the sync (its user signed out); called before each read, write and send, and after each lock wait. */
  guard?: () => void;
  /** This Changeling's origin, which the helper records with a grant. */
  origin?: string;
}
/** The lock one pairing's syncs take, in every tab and the worker, so their sends and acknowledgements don't interleave. */
export const syncLock = (user: string, connection: string) => `remote-relay-sync:${user}:${connection}`;

/** What a record asks the helper for, if anything is still to send: an upsert of a grant, or a revocation. */
function outgoing(g: GrantRecord): { op: 'grant'; scope: Scope } | { op: 'revoke_grant' } | null {
  const s = g.state;
  switch (s.kind) {
    case 'granted': return s.acked ? null : { op: 'grant', scope: s.scope };
    case 'refused': return s.revoking ? { op: 'revoke_grant' } : null;
    case 'revoking': return { op: 'revoke_grant' };
  }
}
/**
 * What `state` becomes once the helper answered what it asked: `rejected`,
 * its reason when the helper refused it for good. A grant acknowledged is
 * acknowledged, one refused is refused; a refused grant's revocation keeps
 * it refused here, however the helper answered; a revocation answered either
 * way is done (null).
 */
function answered(state: GrantState, rejected: string | undefined): GrantState | null {
  switch (state.kind) {
    case 'granted': return rejected === undefined ? { ...state, acked: true } : { kind: 'refused', scope: state.scope, reason: rejected, revoking: false };
    case 'refused': return { ...state, revoking: false };
    case 'revoking': return null;
  }
}

/**
 * Sends each of `connection`'s changes still to send with `send` (`grant`,
 * or `revoke_grant`), revocations before upserts, so a pairing at the
 * helper's cap makes room first (a subject has one record, so it is never
 * revoked and then granted), and records each answer (`answered`) unless
 * the record changed meanwhile; then brings the records in line with the
 * reply's mirror (`reconcileMirror`), sending what that queues too. A
 * rejection (`code: 'rejected'`) is the helper's final answer; any other
 * failure rejects, leaving the rest to send next time. Resolves with whether
 * any record changed.
 */
export async function sync(user: string, connection: string, send: (message: Record<string, unknown>) => Promise<unknown>, { guard = () => {}, origin }: SyncOptions = {}): Promise<boolean> {
  return navigator.locks.request(syncLock(user, connection), async () => {
    let changed = false;
    // Bounded: a reply can queue more changes, which this loop then sends.
    for (let i = 0; i < 2 * MAX_GRANTS; i++) {
      guard();
      const pending = (await grantRecords(user)).flatMap(g => { const out = g.connection === connection ? outgoing(g) : null; return out ? [{ g, out }] : []; });
      const next = pending.find(p => p.out.op === 'revoke_grant') ?? pending[0];
      if (!next) break;
      const { g, out } = next;
      guard();
      let reply: unknown, rejected: string | undefined;
      try { reply = await send(out.op === 'grant' ? { op: 'grant', subject: g.subject, scope: out.scope, ...(origin ? { origin } : {}) } : { op: 'revoke_grant', subject: g.subject }); }
      catch (e) {
        if ((e as { code?: string }).code !== 'rejected') throw e;
        rejected = errorMessage(e).slice(0, 1000) || 'Refused';
      }
      guard();
      await locked(user, async () => {
        guard();
        const all = await grantRecords(user);
        const now = all.find(x => x.connection === connection && sameSubject(x.subject, g.subject));
        if (now && now.updated === g.updated) {
          const state = answered(now.state, rejected);
          await write(user, 'grants', all.flatMap(x => x !== now ? [x] : state ? [{ ...x, state }] : []));
        }
        if (rejected === undefined) await reconcileMirror(user, connection, reply);
      });
      changed = true;
    }
    return changed;
  });
}
